import { useEffect } from 'react';
import { type ReactNode } from 'react';

import { PhoneCallIcon } from '@selina-finance/ui';
import mixpanel from 'mixpanel-browser';

import { HTB_ADVISOR_PHONE_NUMBER } from '@src/shared/constants';
import BackButton from '@src/ui/components/BackButton/BackButton';
import LightbulbCircleIcon from '@src/ui/components/Icons/LightbulbCircleIcon';
import PhoneCallIllustrationDesktopIcon from '@src/ui/components/Icons/PhoneCallIllustrationDesktopIcon';
import PhoneCallIllustrationIcon from '@src/ui/components/Icons/PhoneCallIllustrationIcon';
import WarningTriangleIcon from '@src/ui/components/Icons/WarningTriangleIcon';

interface StepItemProps {
    number: number;
    title: string;
    strapline?: string;
    children: ReactNode;
}

const StepItem = ({ number, title, strapline, children }: StepItemProps) => (
    <div className='flex gap-4 items-start'>
        <div className='flex-shrink-0 w-32px h-32px rounded-full bg-almond-500 flex items-center justify-center font-bold text-sm'>
            {number}
        </div>
        <div>
            <p className='font-sora-bold text-lg lg:text-28 mb-1'>{title}</p>
            {strapline && (
                <p className='font-sora-bold text-sm text-peach-700 mb-2'>
                    {strapline}
                </p>
            )}
            {children}
        </div>
    </div>
);

const HtbNextStepsView = () => {
    useEffect(() => {
        mixpanel.track('DFF HTB Landing Page');
    }, []);

    return (
        <div className='flex flex-col mx-auto max-w-400 w-full lg:max-w-none'>
            <div className='px-0 lg:px-44 flex flex-col lg:flex-row justify-between'>
                <div className='flex flex-col items-center mb-8 lg:max-w-600 order-2 lg:order-1'>
                    <div className='hidden lg:block mb-8 self-start lg:-ml-44'>
                        <BackButton />
                    </div>
                    <div className='flex flex-col flex-1 mb-6 lg:mb-10'>
                        <p className='text-2xl lg:text-4xl font-sora-bold leading-120% tracking-tighter mb-3'>
                            Your adviser will call you soon to continue your
                            application
                        </p>
                        <p className='leading-120%'>
                            They&apos;ll explain next steps and costs, and
                            answer your questions.
                        </p>
                    </div>

                    <div className='flex flex-col items-center bg-almond-300 rounded-lg p-4 w-full mb-8'>
                        <p className='text-sm mb-2'>We&apos;ll call you from</p>
                        <div className='flex items-center'>
                            <PhoneCallIcon />
                            <span className='ml-2 text-lg font-bold'>
                                {HTB_ADVISOR_PHONE_NUMBER}
                            </span>
                        </div>
                        <p className='text-sm mt-2'>
                            Mon–Thu 9am–7pm · Fri 9am–1pm
                        </p>
                    </div>

                    <div className='flex gap-2 items-start rounded-lg border border-peach-400 bg-peach-100 p-3 mb-8 w-full'>
                        <div className='flex-shrink-0'>
                            <WarningTriangleIcon />
                        </div>
                        <div>
                            <p className='font-sora-bold text-lg mb-2'>
                                Fees cannot be refunded
                            </p>
                            <p className='leading-120% text-body mb-3'>
                                You have a Help to Buy equity loan, so
                                you&apos;ll need to pay for a property valuation
                                and a Homes England admin fee.
                            </p>
                            <p className='leading-120% text-body'>
                                If your Selina application is later declined, or
                                you withdraw, you won&apos;t get these back.
                            </p>
                        </div>
                    </div>

                    <div className='flex flex-col flex-1 mb-8 w-full'>
                        <p className='text-2xl lg:text-4xl font-sora-bold leading-120% tracking-tighter mb-3'>
                            How Help to Buy fits in
                        </p>
                        <p className='leading-120%'>
                            Your Help to Buy loan needs to be repaid to get a
                            Selina loan. We&apos;ll add the amount to your loan
                            with us and pay Homes England for you.
                        </p>
                    </div>

                    <div className='flex gap-2 items-start rounded-lg bg-almond-300 p-3 mb-8 w-full'>
                        <div className='flex-shrink-0'>
                            <LightbulbCircleIcon />
                        </div>
                        <div>
                            <p className='leading-120% text-sm mb-3'>
                                Homes England will no longer own a share of your
                                home. What you owe will no longer go up or down
                                with your home&apos;s value.
                            </p>
                            <p className='leading-120% text-sm'>
                                You&apos;ll owe Selina a loan balance plus
                                interest, which may differ from your current
                                Help to Buy interest.
                            </p>
                        </div>
                    </div>

                    <div className='flex flex-col gap-6 w-full'>
                        <StepItem
                            number={1}
                            title='Get your home valued'
                            strapline='Fee based on property, non-refundable'
                        >
                            <p className='leading-120%'>
                                What you owe Homes England is based on your
                                home&apos;s value today, not what you borrowed.
                                We&apos;ll arrange a valuation for you.
                            </p>
                        </StepItem>

                        <StepItem
                            number={2}
                            title='Apply to Homes England'
                            strapline='£200-250, non-refundable'
                        >
                            <p className='leading-120%'>
                                You&apos;ll then apply to Homes England to repay
                                your loan. They&apos;ll review and decide
                                whether to approve it.
                            </p>
                        </StepItem>

                        <StepItem
                            number={3}
                            title='We continue your application'
                        >
                            <p className='leading-120%'>
                                If approved, we&apos;ll continue your
                                application and repay Homes England if your loan
                                completes.
                            </p>
                        </StepItem>
                    </div>
                </div>
                <div className='flex-shrink-0 order-1 lg:order-2 flex justify-center mt-32px lg:mt-0 mb-6 lg:mb-0 lg:self-start'>
                    <span className='lg:hidden'>
                        <PhoneCallIllustrationIcon />
                    </span>
                    <span className='hidden lg:inline'>
                        <PhoneCallIllustrationDesktopIcon />
                    </span>
                </div>
            </div>
        </div>
    );
};

export default HtbNextStepsView;
